import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { WordCommentsCard } from "./WordCommentsCard";
import { WordOutlineCard } from "./WordOutlineCard";
import { SpeakerNotesListCard } from "../../integrations/powerpoint";
import { runHandler } from "../../../integrations/tools";
import {
  stripInventedSectionHeading,
  stripOutlineAlreadyRendered,
  stripStreamingPayloads,
  stripWordPayloads,
} from "../../../integrations/tools/word/utils/parseWordResponse";
import { EmailResultsList } from "../../integrations/email/components/EmailResultsList";
import { DocReviewPanel } from "../../docReview";
import type { RefreshTokenFn } from "../../shared/api/fetchWithRefresh";
import type { ApprovalRequest, ChatMessage, MultipartStep, Pill } from "../types";
import { useOpenCalendarEvent } from "../hooks/useOpenCalendarEvent";
import {
  buildWordDocPills,
  degenerateTableDecline,
  stripStreamingTables,
  declaresTextPill,
  isNonInsertableResponse,
  synthesizeWordDocPills,
} from "../hooks/useChatHelpers";
import { AivaSparkleIcon, PaperclipIcon } from "../../shared/components/icons";
import shared from "../../shared/components/shared.module.css";
import { ActionPills } from "./ActionPills";
import { ApprovalCard } from "./ApprovalCard";
import { MultipartPlanCard } from "./MultipartPlanCard";
import { PlanView } from "./PlanView";
import { WordPillApprovalCard } from "./WordPillApprovalCard";
import styles from "./ChatMessages.module.css";

// Word pills that render as the explicit review/approval card (AIVA-6436,
// scoped in AIVA-6432): batch and document-wide writes only.
//
// This covers exactly five slash commands — the only ones whose write lands in
// places the user never pointed at:
//   word.apply_review  → /review, /passive, /clarity, /citations
//   word.replace_all   → /terms, /replace
//
// The other 30 Word skills stay on the plain pill row. Selection-scoped
// rewrites and single insertions (/improve, /simplify, /write, /outline, …)
// don't need it: the user selected the text themselves, the pill preview IS
// the whole change, and one Ctrl+Z reverts it — a card there is friction with
// nothing to protect. Read-only (/gaps, /structure) and clipboard-only pills
// change nothing to approve.
//
// Gating is handler-based, not skill-based, for two reasons: any future skill
// emitting a batch write inherits the card for free, and the two router skills
// (word_review_router, word_multi) reach apply_review without a slash command
// of their own — same write, same risk, so the same gate.
//
// apply_styles is /format's write (AIVA-8606): it restyles paragraphs across
// the whole document in one click, so the user sees every text → style pair
// before anything changes.
const APPROVAL_CARD_HANDLERS = new Set([
  "word.replace_all",
  "word.apply_review",
  // RV-01's Apply review. A document-wide batch write like the others, and the
  // gate is handler-based precisely so it inherits the card without a new rule.
  "word.apply_review_edits",
  "word.apply_styles",
  // Bates numbering writes to every section's header/footer and is a litigation
  // production step, so the card is where the first stamp and anything already
  // in that region get named before the user commits (DP-02, closing G-12).
  "word.insert_bates",
  // Page numbers write to every section's footer, so the user should see the
  // section count — and anything already in that footer — before approving.
  "word.insert_page_numbers",
]);

// insert_email always gets a card, but reaching it takes one step or two.
//
// It is the one write whose content comes from outside the document, so a review
// surface matters more here than for a restyle, and the pill args already carry
// an `impact_line` written to be read verbatim by the card.
//
// ONE candidate → card immediately. There is nothing to choose, so the only
// question left is "insert this or not", which is exactly what the card asks.
//
// SEVERAL candidates → the row of subject pills comes first and SELECTS; the
// card for the chosen message is what approves. The card's premise is one
// payload with several destinations (insert / append / replace are three places
// for one string), and that is false here: several candidates are several
// DIFFERENT emails. Routing them into one card would describe only the first,
// and routing them to a plain row made a single click paste someone else's
// correspondence into the document with the body never shown. Splitting the
// choice from the approval is what keeps both questions answerable.
const INSERT_EMAIL_HANDLER = "word.insert_email";

/**
 * A user's own turn: any attachment badges, then the text.
 *
 * Its own component so the message loop below stays under the
 * cognitive-complexity limit (sonar typescript:S3776). Nothing about a user
 * message depends on turn state, so it takes only the message.
 */
function UserMessage({ msg }: Readonly<{ msg: ChatMessage }>) {
  return (
    <div className={styles.userMsg}>
      {msg.attachedFileNames?.map((name) => (
        <div key={name} className={styles.attachmentBadge}>
          <span className={styles.attachmentBadgeIcon}><PaperclipIcon /></span>
          <span className={styles.attachmentBadgeName}>{name}</span>
        </div>
      ))}
      {msg.content.trim() && <div className={styles.userBubble}>{msg.content}</div>}
    </div>
  );
}

/**
 * Split this message's pills into the slot cards and the action bar.
 *
 * Extracted from the message loop for cognitive complexity (sonar
 * typescript:S3776) — it was the largest single contributor. Pure, so the two
 * decisions it encodes are readable and testable on their own:
 *
 * 1. WHICH pills exist. Only settled, insertable responses get any: error,
 *    system and guidance messages carry no placeable content (AIVA-5715/5657).
 *    Word turns get the table-aware de-duplicated set (AIVA-5711), and a Word
 *    turn that skipped its pill calls gets the synthesised fallback (AIVA-5927).
 * 2. WHERE they go. CM-01's slot cards sit INSIDE the bubble under a
 *    "suggested times" label (§6.2, Figure 6.2), not in the row beneath it.
 *
 * A picker turn carries NO action bar. Figure 6.2's mock-up ends at the three
 * slot cards — and Figure 5.1, in the same document, lists its five buttons
 * inside the mock-up, so the omission is deliberate rather than a gap in the
 * drawing. §5's "Meeting intent -> Book meeting · Copy only" row is not in
 * conflict: the bar exists so text can be placed into an email ("the standard
 * action bar beneath each response so any output can become a reply", §7), and
 * a slot picker is not placeable text. That row governs a CONVERSATIONAL
 * meeting reply — CM-02's agenda, a calendar read-out — not the picker. It is
 * also the only reading that makes sense to a user: nothing has been chosen
 * yet, so "Book meeting" has no time to book and "Copy" nothing to copy.
 */
/**
 * Whether this turn's heading outline is an ARTEFACT to show, or working
 * material it read on the way to an answer (AIVA-7938).
 *
 * `attachWordOutline` fires on the tool, and several skills call
 * `word_get_document_structure` only to locate a section: /write reads it in
 * STEP 1a, where the prompt says it "writes NOTHING" and "reading the document
 * is preparation, never the answer". Showing the card there put a 100-heading
 * index of the user's own document under a single drafted paragraph.
 *
 * Decided from the declared toolset, not the skill key — the convention this
 * file already follows. A skill that declares a text-placement pill answers with
 * CONTENT, so its structure read was preparation. A skill that declares none
 * answers with a REPORT, and the outline is that report.
 *
 * An absent `allowedTools` means an older stream that does not send them, which
 * must keep behaving as today: show the card.
 */
function outlineIsTheAnswer(allowedTools: string[] | undefined): boolean {
  if (!allowedTools) return true;
  return !declaresTextPill(allowedTools);
}

/**
 * The bubble text, after every render-time strip.
 *
 * TO-02 AC-4 — the outline contract asks for a one-line confirmation PLUS a
 * JSON payload. The confirmation is the message; the payload is machinery, and
 * a user seeing raw JSON in the bubble would reasonably conclude the add-in had
 * malfunctioned. Stripped at render rather than on receipt so the payload stays
 * available to the pill builder, which needs it to construct Variant B.
 *
 * stripWordPayloads / stripInventedSectionHeading: the same strips the
 * placement pill applies, so the bubble shows the text that will actually be
 * inserted (AIVA-7938). stripOutlineAlreadyRendered: drop any heading line the
 * outline card below is already showing — a stale prompt still transcribes the
 * list, which put the same 171 headings on screen twice. Matched against the
 * card's own rows, so prose and unlisted headings stay. Rows are handed over
 * only when the card actually renders (`outlineCardShown`): headings removed
 * from the bubble must be on screen somewhere else.
 */
function agentDisplayContent(msg: ChatMessage, outlineCardShown: boolean, isStreaming = false): string {
  if (!msg.content) return "";
  // Still streaming. Two things must not reach the bubble yet:
  //  - a table, because no skill information has arrived and it may be withdrawn;
  //  - a review/outline PAYLOAD, because stripWordPayloads can only remove one
  //    once its closing brace has streamed, so the raw {"review":[{"matchText"…
  //    text rendered and then vanished on completion (§4.1 AC-4 — the user must
  //    never see the payload as raw JSON).
  if (isStreaming) return stripStreamingTables(stripStreamingPayloads(msg.content));
  // A /table reply whose table is a dump is shown as the decline it should have
  // been. The buttons are already suppressed; leaving the table on screen made
  // the fix look absent (AIVA-8038) — see degenerateTableDecline.
  const decline = degenerateTableDecline(msg.content, msg.allowedTools);
  if (decline) return decline;
  const cardRows = outlineCardShown ? msg.wordOutline?.rows.map((r) => r.text) : undefined;
  return stripOutlineAlreadyRendered(stripInventedSectionHeading(stripWordPayloads(msg.content)), cardRows);
}

function resolvePillSets(
  msg: ChatMessage,
  hasWordSelection: boolean,
  showSettledArtifacts: boolean,
  isGuardrailNotice: boolean,
): { slotPills: Extract<Pill, { type: "chat_intent" }>[]; barPills: Pill[] } {
  const eligible = showSettledArtifacts && !isGuardrailNotice && !isNonInsertableResponse(msg.content);
  const emitted = msg.actionPills ?? [];

  let renderedPills: Pill[] = [];
  // An outline the ADD-IN read is enough on its own — checked before the
  // "did the model emit anything?" branches below, because the reported bug was
  // that it emitted NOTHING. Under the old prompt the model spent its whole
  // output transcribing 171 headings, truncated, and never reached its
  // pill_word_insert_outline call; `emitted` was empty, so the only branch that
  // ran was the content-synthesis one, which knows how to make Copy and nothing
  // else. Hence a full 171-row card with a lone Copy button under it.
  //
  // Covers /structure too, which declares only pill_word_copy_text: it had NO
  // pills at all when the model didn't reach its tool call, because
  // synthesizeWordDocPills refuses a skill that declares no TEXT pill and
  // 'copy' is its own kind. buildWordDocPills decides the shape from the
  // declared tools — Insert+Copy for /outline, Copy alone for /structure.
  // Same substitution the bubble makes: a dump turns into the decline, and a
  // decline carries no pills at all — not even Copy, which would otherwise put
  // the rejected table on the clipboard.
  if (degenerateTableDecline(msg.content, msg.allowedTools)) return { slotPills: [], barPills: [] };
  const outlineRows = msg.wordOutline?.rows.length ?? 0;
  const declaresOutlinePill =
    (msg.allowedTools?.includes("pill_word_insert_outline") ?? false) ||
    (msg.allowedTools?.includes("pill_word_copy_text") ?? false);
  // outlineIsTheAnswer: pill_word_copy_text alone is NOT enough to route here —
  // nearly every Word skill declares it. /format read the structure (to fix it),
  // wordOutline attached, and this branch swallowed its four placement pills,
  // leaving a lone Copy. A skill that places text answers with CONTENT; the
  // outline shapes its pills only when the skill has no text pill at all.
  if (eligible && outlineRows > 0 && declaresOutlinePill && outlineIsTheAnswer(msg.allowedTools)) {
    renderedPills = buildWordDocPills(
      emitted,
      hasWordSelection,
      msg.content,
      msg.allowedTools,
      msg.committable,
      msg.wordOutline,
    );
  } else if (eligible && emitted.length > 0) {
    const isWordDocContext = emitted.some((p) => p.type === "office_action" && p.handler.startsWith("word."));
    renderedPills = isWordDocContext
      ? buildWordDocPills(emitted, hasWordSelection, msg.content, msg.allowedTools, msg.committable)
      : emitted;
  } else if (eligible && emitted.length === 0 && !!msg.content) {
    renderedPills = synthesizeWordDocPills(
      hasWordSelection,
      msg.content,
      msg.allowedTools,
      msg.skillKey,
      msg.committable,
    );
  }

  const slotPills = renderedPills.filter(
    (p): p is Extract<Pill, { type: "chat_intent" }> => p.type === "chat_intent" && p.variant === "slot",
  );
  const barPills = slotPills.length > 0 ? [] : renderedPills.filter((p) => !slotPills.includes(p as never));
  return { slotPills, barPills };
}


/**
 * A locked (already-sent) selection wins outright — it's the historical
 * record of what THIS message's turn actually carried. Absent a lock, only
 * the LATEST message reflects the live, still-being-composed selection; an
 * older, never-acted-on disambiguation shows nothing; ids are just
 * display-name text, so falling back for every message risks an unrelated
 * older row appearing checked from a same-name collision on the CURRENT
 * selection.
 *
 * Extracted from the message loop for cognitive complexity (sonar
 * typescript:S3776) — the nested ternary it replaces was flagged directly.
 */
function resolveNameCandidateSelectedIds(
  lockedIds: string[] | undefined,
  isLatestMessage: boolean,
  liveSelectedIds: ReadonlySet<string> | undefined,
): ReadonlySet<string> | undefined {
  if (lockedIds) return new Set(lockedIds);
  if (isLatestMessage) return liveSelectedIds;
  return undefined;
}

function relativeTime(ts: number | undefined, now: number): string {
  const clockTime = (t: number) =>
    new Date(t).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (!ts) return clockTime(now);
  const diffSec = Math.floor((now - ts) / 1000);
  if (diffSec < 60) return "just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return diffMin === 1 ? "1 min ago" : `${diffMin} mins ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return diffHr === 1 ? "1 hr ago" : `${diffHr} hrs ago`;
  const diffDay = Math.floor(diffHr / 24);
  return diffDay === 1 ? "yesterday" : `${diffDay} days ago`;
}

const TOOL_LABELS: Record<string, string> = {
  // Backend tools — Graph calls + draft submissions
  fetch_emails: "Fetching emails",
  fetch_calendar_events: "Checking calendar",
  manage_calendar: "Checking availability",
  fetch_conversation_thread: "Reading email thread",
  fetch_email_attachments: "Reading attachments",
  summarize_email_context: "Reading the email, thread and attachments",
  compare_attachments: "Comparing attachments",
  find_user_by_name: "Looking up contacts",
  get_nbas: "Generating recommendations",
  resolve_persona: "Looking up persona",
  submit_email_draft: "Preparing draft",
  submit_meeting_draft: "Preparing meeting",
  submit_teams_meeting_draft: "Preparing Teams meeting",
  list_emails_obo: "Searching inbox",
  submit_email_search_results: "Preparing results",
  // Frontend pill tools — flash briefly while rendering action buttons
  pill_office_compose_reply: "Preparing reply",
  pill_office_compose_open: "Preparing draft",
  pill_office_clipboard_copy: "Preparing copy",
  pill_office_calendar_open: "Preparing calendar event",
  pill_chat_capability_launch: "Launching capabilities",
  pill_chat_refine_inbox: "Suggesting refinements",
  pill_chat_refine_search: "Suggesting refinements",
  pill_chat_tone_adjustment: "Suggesting tones",
  pill_chat_book_slot: "Suggesting slots",
  pill_chat_meeting_followup: "Suggesting follow-ups",
};

// word_docreview_judge is one opaque tool call from the model's perspective —
// internally it runs up to ~20 concurrent LLM judgment calls (topic_mismatch,
// exhibit_schedule_mismatch's ambiguous case, hyperlink_target_mismatch,
// used_not_defined) that together can take 20-40s, but the backend's
// v2 `activity` line for it is a single static label for that whole window.
// Rather than sitting on unchanging text that long, rotate through a few
// labels naming the actual rules running underneath it — genuinely accurate
// (all four really are in flight at once), just not literally counted
// progress. True per-item progress (e.g. "8/20 checked") would need a new
// LangGraph custom stream mode this repo doesn't have yet — this rotation is
// the version that fits today's tool_start/activity plumbing with no new
// backend wiring.
//
// AIVA-8194: the rotation names only the checks this review was scoped to.
// Listing all four on a single-rule ask read as "it is checking everything".
// The scope is the rules list the preceding word.docreview.scan call was
// given (the judge's own arguments never reach the client — tool_start only
// forwards the first, empty argument chunk). With no list the scan was a full
// review — the skill prompts pass a list on every scoped check — so all four
// judgment checks really are running and the original rotation stays.
const DOCREVIEW_JUDGE_TOOL_NAME = "word_docreview_judge";
const TOPIC_LABEL = "Checking cross-reference topics…";
const EXHIBIT_LABEL = "Checking Exhibit/Schedule mentions…";
const HYPERLINK_TARGET_LABEL = "Checking hyperlink targets…";
const UNDEFINED_TERMS_LABEL = "Checking for undefined terms…";
// AIVA-8297 follow-up: defined_term_section_mismatch is judged too, so a full
// review names it alongside the other four.
const DEFINED_TERM_SECTION_LABEL = "Checking defined terms' sections…";
const DOCREVIEW_RULE_LABELS: Readonly<Record<string, string>> = {
  topic_mismatch: TOPIC_LABEL,
  exhibit_schedule_ambiguous: EXHIBIT_LABEL,
  exhibit_schedule_mismatch: EXHIBIT_LABEL,
  hyperlink_target_mismatch: HYPERLINK_TARGET_LABEL,
  used_not_defined: UNDEFINED_TERMS_LABEL,
  missing_hyperlink: "Checking for missing hyperlinks…",
  broken_reference: "Checking cross-reference targets…",
  bracket_heading_mismatch: "Checking bracketed headings…",
  inconsistent_formatting: "Checking cross-reference formatting…",
  defined_term_section_mismatch: DEFINED_TERM_SECTION_LABEL,
  shortened_hyperlink_text: "Checking cross-references' shown text…",
  defined_not_used: "Checking for unused defined terms…",
  alphabetical_order: "Checking definitions order…",
  numbering_order: "Checking heading numbering…",
  missing_defined_term: "Checking for missing definitions…",
  ambiguous_reference: "Checking for ambiguous references…",
};
const DOCREVIEW_JUDGE_UNSCOPED_LABELS = [
  TOPIC_LABEL,
  EXHIBIT_LABEL,
  HYPERLINK_TARGET_LABEL,
  UNDEFINED_TERMS_LABEL,
  DEFINED_TERM_SECTION_LABEL,
];
const DOCREVIEW_JUDGE_ROTATION_INTERVAL_MS = 3500;

/** The judge step's rotating labels for `scope` — one per distinct check, in
 *  the order the rules were given; the full-review rotation when nothing maps. */
function docReviewJudgeLabels(scope: readonly string[] | null | undefined): string[] {
  const labels = new Set<string>();
  for (const rule of scope ?? []) {
    const label = DOCREVIEW_RULE_LABELS[rule.toLowerCase()];
    if (label) labels.add(label);
  }
  return labels.size > 0 ? [...labels] : DOCREVIEW_JUDGE_UNSCOPED_LABELS;
}

interface ChatMessagesProps {
  readonly messages: ChatMessage[];
  readonly streaming: boolean;
  readonly activeTool: string | null;
  readonly chatEndRef: React.RefObject<HTMLDivElement>;
  /** Boot greeting (ST-02), rendered as the thread's persistent first bubble.
   *  Presentational only — never included in the conversation context. */
  readonly greeting: string;
  readonly onPillClick: (pill: Pill) => void | Promise<void>;
  /** Ids of the name-candidate pills currently selected — drives each pill's
   *  toggled visual state. See useChat's selectedNameCandidateIds. */
  readonly selectedNameCandidateIds?: ReadonlySet<string>;
  readonly hasWordSelection?: boolean;
  /** Sanitised backend progress line (v2 `activity` events). Takes precedence
   *  over the TOOL_LABELS mapping when present. */
  readonly activityLine?: string | null;
  /** AIVA-8194 — rule keys the latest doc-review scan was scoped to; drives
   *  which checks the judge step's progress line names. */
  readonly docReviewScope?: readonly string[] | null;
  /** Approve/Deny click on an in-chat approval card (v2 HITL). */
  readonly onApprovalDecision?: (
    messageId: string,
    approval: ApprovalRequest,
    decision: "approve" | "reject",
  ) => void | Promise<void>;
  /** Run / skip / cancel on a sequential-multipart plan card. Omitted on hosts
   *  where the planner never fires, which is what keeps the card off Outlook. */
  readonly onMultipartStep?: (
    messageId: string,
    action: "run" | "skip" | "cancel",
    step?: MultipartStep,
  ) => void | Promise<void>;
  /** AIVA-6436 — Amend feedback on a Word write-pill approval card. Sends a
   *  follow-up chat turn so the LLM regenerates the suggestion. */
  readonly onPillAmend?: (pill: Pill, feedback: string) => void | Promise<void>;
  /** Re-runs the prompt behind a user-stopped reply (Regenerate button). */
  readonly onRegenerate?: (assistantMessageId: string) => void;
  readonly onPillBooked?: (messageId: string, pillLabel: string) => void;
  /** AIVA-7506 — the check-references thread, so the inline doc-review card
   *  can fetch its findings. Null until a scan has run this session. */
  readonly docReviewThreadId?: string | null;
  /** True once word.docreview.anchor_findings has dispatched this session —
   *  useChat's docReviewReadyRef, read at render time. */
  readonly docReviewReady?: boolean;
  readonly apiToken?: string | null;
  readonly refreshTokens?: RefreshTokenFn;
}

export function ChatMessages({
  messages,
  streaming,
  activeTool,
  chatEndRef,
  greeting,
  onPillClick,
  selectedNameCandidateIds,
  hasWordSelection = false,
  activityLine = null,
  docReviewScope = null,
  onApprovalDecision,
  onMultipartStep,
  onPillAmend,
  onRegenerate,
  onPillBooked,
  docReviewThreadId = null,
  docReviewReady = false,
  apiToken = null,
  refreshTokens,
}: Readonly<ChatMessagesProps>) {
  const openCalendarEvent = useOpenCalendarEvent();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 10_000);
    return () => clearInterval(id);
  }, []);

  // See DOCREVIEW_RULE_LABELS's own comment. Resets to index 0
  // (and stops rotating) the moment activeTool moves off word_docreview_judge
  // — the next tool's own activity label (or none) takes over immediately
  // rather than a stale rotation index lingering into a later step.
  const judgeLabels = useMemo(() => docReviewJudgeLabels(docReviewScope), [docReviewScope]);
  const [judgeLabelIndex, setJudgeLabelIndex] = useState(0);
  useEffect(() => {
    if (activeTool !== DOCREVIEW_JUDGE_TOOL_NAME || judgeLabels.length < 2) {
      setJudgeLabelIndex(0);
      return;
    }
    const id = setInterval(() => {
      setJudgeLabelIndex((i) => (i + 1) % judgeLabels.length);
    }, DOCREVIEW_JUDGE_ROTATION_INTERVAL_MS);
    return () => clearInterval(id);
  }, [activeTool, judgeLabels]);

  // Which email candidate the user picked, per message id (see the pill routing
  // below). Held here rather than in ActionPills because the choice outlives the
  // row: picking one REPLACES the row with that candidate's approval card, and
  // rejecting brings the row back.
  const [emailChoice, setEmailChoice] = useState<Record<string, number>>({});

  // Smooth-follow scroll (AIVA-5054). Re-runs on every messages-array change
  // — covers prompt submit, pill click, token streaming, pill emission, and
  // search-results attach — so the loading indicator and any newly attached
  // action pills stay in view. Tracks whether the user has scrolled away
  // from the bottom; if they have, we don't yank them back down. The
  // sentinel below the last message has a margin so it parks slightly above
  // the input bar, leaving the pills visible with breathing room.
  const scrollerRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef(true);
  const wasStreamingRef = useRef(false);
  const savedScrollTopRef = useRef(0);

  const handleScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    savedScrollTopRef.current = el.scrollTop;
    // Treat "near the bottom" as within 80 px — covers small rendering
    // drift while still letting a deliberate scroll-up disable follow.
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = distanceFromBottom < 80;
  };

  // Restore scroll position after the element is unhidden (display:none on an
  // ancestor resets scrollTop in some browsers, including WebView2). Track
  // 0-height → non-zero transitions via ResizeObserver and put the scroll
  // container back where the user left it.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    let wasHidden = false;
    const observer = new ResizeObserver(() => {
      if (el.clientHeight === 0) {
        wasHidden = true;
      } else if (wasHidden) {
        wasHidden = false;
        el.scrollTop = savedScrollTopRef.current;
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    // A new response begins loading whenever streaming flips on — a prompt
    // submit or an action-pill selection both run a chat turn that sets
    // streaming=true and appends an empty assistant bubble. Snap to that
    // loading section even if the user had scrolled up to read older content,
    // so progress is visible immediately rather than only once the response
    // has finished (AIVA-5054). This generalises the Word-only force-scroll
    // (AIVA-5232) to every host, fixing the Outlook regression where the chat
    // never auto-scrolled after submitting a prompt or selecting a pill.
    if (streaming && !wasStreamingRef.current) {
      stickToBottomRef.current = true;
    }
    wasStreamingRef.current = streaming;

    if (!stickToBottomRef.current) return;

    const el = chatEndRef.current;
    // jsdom (test env) doesn't implement scrollIntoView; guard so unit
    // tests that only assert message rendering don't crash on this side
    // effect.
    if (el && typeof el.scrollIntoView === "function") {
      // Snap (no animation) while streaming: a smooth scroll lags behind
      // rapidly arriving tokens, which trips the distance-from-bottom check
      // in handleScroll and falsely disables follow mid-response. Smooth
      // returns once the stream ends, covering pill attachment and the rest
      // of the panel's life.
      el.scrollIntoView({ behavior: streaming ? "auto" : "smooth", block: "end" });
    }
  }, [messages, chatEndRef, streaming]);


  return (
    <div className={styles.chat} ref={scrollerRef} onScroll={handleScroll}>
      {/* The greeting is an agent bubble at the head of the thread, not a
          centred placeholder — Figure 3.1 draws it that way in both specs, and
          Word's greeting is a bulleted capability list that needs Markdown.
          It persists above the transcript and scrolls with it, but it is
          presentational only — never part of `messages`, so never sent to the
          backend. New chat clears `messages` and it heads the thread again (ST-01). */}
      <div className={styles.agentMsg} data-testid="chat-greeting">
        <div className={styles.agentAvatar}>
          <AivaSparkleIcon />
        </div>
        <div className={styles.agentBody}>
          <div className={styles.agentMeta}>
            <strong className={styles.agentMetaBrand}>AiVA</strong> &middot; just now
          </div>
          <div className={styles.agentText}>
            <Markdown remarkPlugins={[remarkGfm]}>{greeting}</Markdown>
          </div>
        </div>
      </div>

      {messages.map((msg, i) => {
        if (msg.role === "user") {
          return <UserMessage key={msg.id} msg={msg} />;
        }

        const isLastMsg = streaming && i === messages.length - 1;
        // Unlike isLastMsg above, not gated on `streaming` — this decides
        // whether a name-candidate row falls back to the LIVE selection
        // (see nameCandidateSelectedIds below), which matters whenever this
        // is simply the most recent message, streaming or not.
        const isLatestMessage = i === messages.length - 1;
        // The v2 backend `activity` line wins; otherwise fall back to the
        // legacy TOOL_LABELS mapping — and to a generic friendly label rather
        // than the raw tool name, so tools without an entry don't leak their
        // internal id (e.g. ``list_emails_obo``) into the user-facing bubble.
        const legacyToolLabel = activeTool
          ? `${TOOL_LABELS[activeTool] ?? "Working on it"}…`
          : null;
        // word_docreview_judge overrides even the (otherwise higher-priority)
        // activity line — see DOCREVIEW_RULE_LABELS's own comment
        // for why its single static label needs this.
        const toolLabel =
          activeTool === DOCREVIEW_JUDGE_TOOL_NAME
            ? (judgeLabels[judgeLabelIndex] ?? judgeLabels[0])
            : (activityLine ?? legacyToolLabel);
        const isGuardrailNotice =
          !!msg.guardrailCategories && msg.guardrailCategories.length > 0;
        // Cards, calendar list and action pills are turn-completion artefacts — keep
        // them hidden while this message is still streaming so they don't appear
        // before the response text finishes generating (AIVA-5101). A STOPPED
        // message hides them permanently: results/pills attach mid-stream, and
        // without this, clicking Stop is precisely what revealed them — the stop
        // ended `streaming`, which flipped this gate open (AIVA-7149/7148/7081).
        // The data stays on the message; only rendering is suppressed, so
        // Regenerate and thread rehydration are unaffected.
        const showSettledArtifacts = (!isLastMsg || !streaming) && !msg.stopped;

        // Which pills exist and where they go — see resolvePillSets.
        const { slotPills, barPills } = resolvePillSets(
          msg,
          hasWordSelection,
          showSettledArtifacts,
          isGuardrailNotice,
        );

        // Mirrors the WordOutlineCard render condition below. The outline strip
        // may only fire when the card ACTUALLY renders — headings removed from
        // the bubble must be on screen somewhere else, and an all-list reply is
        // allowed to strip to nothing only because the card says the same thing.
        const outlineCardShown =
          showSettledArtifacts && msg.wordOutline !== undefined && outlineIsTheAnswer(msg.allowedTools);
        const displayContent = agentDisplayContent(msg, outlineCardShown, isLastMsg);

        let agentContent: React.ReactNode = null;
        if (displayContent) {
          agentContent = <Markdown remarkPlugins={[remarkGfm]}>{displayContent}</Markdown>;
        } else if (isLastMsg && !msg.content) {
          agentContent = (
            <span className={shared.dots} aria-hidden="true">
              <span className={shared.dot} />
              <span className={shared.dot} />
              <span className={shared.dot} />
            </span>
          );
        }

        return (
          <div key={msg.id} className={styles.agentMsg}>
            <div className={styles.agentAvatar}>
              <AivaSparkleIcon />
            </div>
            <div className={styles.agentBody}>
              <div className={styles.agentMeta}>
                <strong className={styles.agentMetaBrand}>AiVA</strong> &middot;{" "}
                {slotPills.length > 0
                  ? "suggested times"
                  : relativeTime(msg.timestamp, now)}
              </div>
              {isLastMsg && toolLabel && (
                // §9.1 announcements: the status region is aria-live="polite" so
                // a screen reader reads each progress line ("Searching the
                // mailbox…", "Suggesting slots…") as it changes.
                <div className={styles.toolIndicator} aria-live="polite">
                  {toolLabel}
                </div>
              )}
              {msg.reasoning && (
                <details className={styles.thinkingBlock}>
                  <summary className={styles.thinkingSummary}>
                    {(() => {
                      if (isLastMsg && !msg.content) return "Thinking…";
                      if (msg.reasoningDurationMs) {
                        return `Thought for ${Math.max(1, Math.round(msg.reasoningDurationMs / 1000))}s`;
                      }
                      return "Thoughts";
                    })()}
                  </summary>
                  <div className={styles.thinkingContent}>{msg.reasoning}</div>
                </details>
              )}
              {msg.plan && msg.plan.length > 0 && <PlanView items={msg.plan} />}
              {isGuardrailNotice && (
                <div className={styles.guardrailNotice} role="alert">
                  {msg.content}
                </div>
              )}
              {/* Only render the bubble when there is something to put in it.
                  `.agentText` carries its own background, border and padding, so
                  an empty one draws a blank box that reads as a failed response —
                  seen above approval cards, and on any rehydrated message whose
                  content is empty. */}
              {/* Two changes met here and both are needed. AIVA-7026 renders the
                  slot cards INSIDE the bubble (Figure 6.2 draws them there, not
                  in the capsule row below), which is why the bubble now also
                  shows when there is no text but there are cards. AIVA-6996 added
                  the aria-live status region on the last message (§9.1). */}
              {!isGuardrailNotice && (agentContent || slotPills.length > 0) && (
                <div
                  className={styles.agentText}
                  {...(isLastMsg
                    ? { role: "status", "aria-live": "polite" as const }
                    : {})}
                >
                  {agentContent}
                  {slotPills.length > 0 && (
                    <ActionPills
                      pills={slotPills}
                      onClick={onPillClick}
                      // Same lock wiring as the capsule row below. Without these
                      // the slot picker would not stay locked across a reload —
                      // and G-9 is specifically about the slot picker, so it has
                      // to be passed here above all (AIVA-6986).
                      bookedLabel={msg.bookedPillLabel}
                      onBook={
                        onPillBooked
                          ? (pill) => onPillBooked(msg.id, pill.label)
                          : undefined
                      }
                    />
                  )}
                </div>
              )}
              {msg.stopped && (
                <div className={styles.stoppedRow}>
                  <span className={styles.stoppedBadge}>Stopped by you</span>
                  {onRegenerate && (
                    <button
                      type="button"
                      className={styles.regenerateBtn}
                      onClick={() => onRegenerate(msg.id)}
                      disabled={streaming}
                    >
                      Regenerate
                    </button>
                  )}
                </div>
              )}
              {msg.approval && onApprovalDecision && !msg.stopped && (
                <ApprovalCard
                  approval={msg.approval}
                  // Disable every card while any turn is streaming — clicking a
                  // second approval before the first's resume has settled is
                  // what let two concurrent resumes race and clobber each
                  // other's message slot (see turnEvents.ts findAssistantIndex).
                  busy={streaming}
                  onDecision={(decision) =>
                    onApprovalDecision(
                      msg.id,
                      msg.approval as ApprovalRequest,
                      decision,
                    )
                  }
                />
              )}
              {/* AIVA-6431 — each step's card renders on the message it was
                  asked under and stays there: the proposal carries step 1's
                  question, and every later step's card is anchored to the answer
                  it follows. Nothing relocates as the queue progresses. */}
              {msg.multipartPlan && onMultipartStep && (
                <MultipartPlanCard
                  plan={msg.multipartPlan}
                  busy={streaming}
                  onRunStep={(step) => onMultipartStep(msg.id, "run", step)}
                  onSkipStep={(step) => onMultipartStep(msg.id, "skip", step)}
                  onCancel={() => onMultipartStep(msg.id, "cancel")}
                />
              )}
              {showSettledArtifacts && msg.wordComments !== undefined && (
                <WordCommentsCard
                  comments={msg.wordComments}
                  // Row actions run their frontend tool directly rather than
                  // going through a pill: they are per-row, and a pill row of
                  // "Resolve #1 / Delete #1 / Resolve #2…" is unreadable. The
                  // handlers are the same ones the backend would dispatch.
                  onResolve={async (id) => {
                    // ResumeResult envelope: ok is the transport result, and the
                    // handler's own { ok } lives under data. Both must be true for
                    // the row to claim the document changed.
                    const out = (await runHandler("word", "word.resolve_comment", { comment_id: id }, 5000)) as
                      | { ok?: boolean; data?: { ok?: boolean } }
                      | undefined;
                    return out?.ok === true && out.data?.ok !== false;
                  }}
                  onDelete={async (id) => {
                    const out = (await runHandler("word", "word.delete_comment", { comment_id: id }, 5000)) as
                      | { ok?: boolean; data?: { ok?: boolean } }
                      | undefined;
                    return out?.ok === true && out.data?.ok !== false;
                  }}
                />
              )}
              {showSettledArtifacts && msg.speakerNotesList !== undefined && (
                // DI-02 — read locally from the deck; the card carries its own header.
                <SpeakerNotesListCard result={msg.speakerNotesList} />
              )}
              {outlineCardShown && msg.wordOutline !== undefined && (
                // Read-only: copying is the action bar's Copy pill, which shares
                // its sections with Insert outline (N). The card renders, nothing more.
                <WordOutlineCard outline={msg.wordOutline} />
              )}
              {showSettledArtifacts && msg.searchResults !== undefined && (
                <EmailResultsList
                  querySummary={msg.searchQuerySummary ?? "Search results"}
                  results={msg.searchResults}
                  // Mailbox searches only — the calendar card below deliberately
                  // passes none, since a day's events are not a capped search.
                  searchWindow={msg.searchWindow}
                />
              )}
              {showSettledArtifacts && msg.calendarSources !== undefined && (
                <EmailResultsList
                  querySummary="Calendar events"
                  results={msg.calendarSources}
                  onItemClick={(r) => openCalendarEvent(r.id, r.webLink)}
                />
              )}
              {barPills.length > 0 && <hr className={styles.pillDivider} />}
              {barPills.length > 0 && (() => {
                // AIVA-6436: the turn's batch / document-wide Word writes get
                // one explicit Approve / Reject / Amend surface — a single card
                // whose apply buttons are the pills (see APPROVAL_CARD_HANDLERS
                // for the risk rationale). Everything else — selection-scoped
                // Word writes, copy / clear-highlights, chat_intent, non-Word
                // office_action — keeps the plain pill-row UX.
                const emailPills = barPills.filter(
                  (p): p is Extract<Pill, { type: "office_action" }> =>
                    p.type === "office_action" && p.handler === INSERT_EMAIL_HANDLER,
                );
                // See INSERT_EMAIL_HANDLER: several candidates are a choice
                // first and an approval second, so they stay OUT of the shared
                // card and their row selects instead of inserting.
                const manyEmails = emailPills.length > 1;
                const chosenIdx = emailChoice[msg.id];
                const chosenEmail = manyEmails && chosenIdx !== undefined ? emailPills[chosenIdx] : undefined;
                const needsApprovalCard = (p: Pill): p is Extract<Pill, { type: "office_action" }> =>
                  p.type === "office_action" &&
                  (APPROVAL_CARD_HANDLERS.has(p.handler) ||
                    (p.handler === INSERT_EMAIL_HANDLER && !manyEmails));
                const cardPills = barPills.filter(needsApprovalCard);
                const plainPills = barPills.filter((p) => !needsApprovalCard(p));
                // While one candidate is under review the others are hidden: the
                // card IS the pending decision, and leaving the rest clickable
                // invites a second email landing on top of the first.
                const rowPills = chosenEmail
                  ? plainPills.filter(
                      (p) => !(p.type === "office_action" && p.handler === INSERT_EMAIL_HANDLER),
                    )
                  : plainPills;
                const nameCandidateSelectedIds = resolveNameCandidateSelectedIds(
                  msg.lockedNameCandidateIds,
                  isLatestMessage,
                  selectedNameCandidateIds,
                );
                const handleRowClick = (p: Pill) => {
                  const idx = emailPills.indexOf(p as Extract<Pill, { type: "office_action" }>);
                  if (manyEmails && idx >= 0) {
                    setEmailChoice((prev) => ({ ...prev, [msg.id]: idx }));
                    return;
                  }
                  // Stamped at click time, not emission time: the pill renders
                  // mid-stream, before the `done` event sets msg.skillKey.
                  // find_user_by_name is wired up for several skills, so the
                  // click handler needs to know which one produced THIS
                  // disambiguation before it decides on a lead-in sentence.
                  if (p.type === "name_candidate") {
                    return onPillClick({ ...p, skillKey: msg.skillKey });
                  }
                  return onPillClick(p);
                };
                // Rejecting a candidate returns to the row rather than ending the
                // turn: the alternative is a dead end, because the candidates
                // live only in these pills and the next turn has never seen them.
                const clearChoice = () =>
                  setEmailChoice((prev) => {
                    const next = { ...prev };
                    delete next[msg.id];
                    return next;
                  });
                return (
                  <>
                    {cardPills.length > 0 && (
                      <WordPillApprovalCard
                        pills={cardPills}
                        onApprove={onPillClick}
                        onAmend={onPillAmend ? (p, fb) => onPillAmend(p, fb) : undefined}
                      />
                    )}
                    {chosenEmail && (
                      // Keyed by candidate so switching resets the card's own
                      // decision/busy state instead of inheriting the last one's.
                      <WordPillApprovalCard
                        key={`email-choice-${msg.id}-${chosenIdx}`}
                        pills={[chosenEmail]}
                        onApprove={onPillClick}
                        onReject={clearChoice}
                        onAmend={
                          onPillAmend
                            ? async (p, fb) => {
                                await onPillAmend(p, fb);
                                clearChoice();
                              }
                            : undefined
                        }
                      />
                    )}
                    {rowPills.length > 0 && (
                      // rowPills / handleRowClick (email candidates select rather
                      // than insert) compose with the booking props from main —
                      // the book affordance is per-pill and orthogonal to which
                      // pills the row is showing.
                      <ActionPills
                        pills={rowPills}
                        onClick={handleRowClick}
                        bookedLabel={msg.bookedPillLabel}
                        onBook={onPillBooked ? (pill) => onPillBooked(msg.id, pill.label) : undefined}
                        selectedNameCandidateIds={nameCandidateSelectedIds}
                      />
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        );
      })}
      {/* AIVA-7506: a ref flag rather than per-message state (see useChat's
          docReviewReadyRef) — rendered once, after every message, rather
          than attached to one that later token-streaming could replace
          wholesale and silently drop the flag from. */}
      {!streaming && docReviewReady && (
        <DocReviewPanel threadId={docReviewThreadId} apiToken={apiToken} refreshTokens={refreshTokens} />
      )}
      <div ref={chatEndRef} className={styles.scrollSentinel} />
    </div>
  );
}
